import { UserConsentDto, UserDto } from '@ecdlink/core';
import {
  UserConsentInput,
  UserModelInput,
  UserSyncStatus,
} from '@ecdlink/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserService } from '@services/UserService';
import { RootState, ThunkApiType } from '../types';
import { UserResetPasswrodParams } from './user.types';
import { newGuid } from '@/utils/common/uuid.utils';

export const getUser = createAsyncThunk<
  UserDto,
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'getUser',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      user: { user: userCache },
    } = getState();

    const cacheBelongsToCurrentUser =
      !!userCache && !!userAuth?.id && userCache.id === userAuth.id;

    if (cacheBelongsToCurrentUser) {
      return userCache as UserDto;
    }

    try {
      let user: UserDto | undefined;

      if (userAuth?.auth_token) {
        user = await new UserService(userAuth?.auth_token).getUser();
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!user) {
        return rejectWithValue('Error getting User');
      }

      return user;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getUserConsents = createAsyncThunk<
  UserConsentDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  'getUserConsents',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      user: { userConsent: userConsentCache },
    } = getState();

    const cacheBelongsToCurrentUser =
      !!userConsentCache &&
      !!userAuth?.id &&
      userConsentCache.every(
        (consent) => !consent.userId || consent.userId === userAuth.id
      );

    if (cacheBelongsToCurrentUser) {
      return userConsentCache;
    }

    try {
      let userConsent: UserConsentDto[] | undefined;

      if (userAuth?.auth_token) {
        userConsent = await new UserService(
          userAuth?.auth_token
        ).getUserConsents(userAuth.id);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!userConsent) {
        return rejectWithValue('Error getting User Consent');
      }

      return userConsent;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertUserConsents = createAsyncThunk<
  any,
  UserConsentDto,
  ThunkApiType<RootState>
>('upsertUserConsents', async (input, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    user: { userConsent },
  } = getState();

  try {
    if (userAuth?.auth_token && input.id) {
      return await new UserService(userAuth?.auth_token).updateUserConsents(
        input.id,
        mapConsent(input)
      );
    }

    const unsyncedConsent = userConsent?.filter((consent) => !consent.synced);

    let promises: Promise<{
      id: string;
      userId: string;
      consentId: number;
      consentType?: string | null;
    }>[] = [];

    if (userAuth?.auth_token && !!unsyncedConsent?.length) {
      promises = unsyncedConsent.map(async (x) => {
        const input: UserConsentInput = {
          Id: x.id,
          ConsentId: x.consentId,
          ConsentType: x.consentType,
          CreatedUserId: x.createdUserId,
          UserId: x.userId,
          IsActive: true,
        };

        return await new UserService(userAuth?.auth_token).updateUserConsents(
          x.id ?? '',
          input
        );
      });
    }
    return Promise.all(promises);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const resetUserPassword = createAsyncThunk<
  boolean,
  UserResetPasswrodParams,
  ThunkApiType<RootState>
>(
  'resetUserPassword',
  // eslint-disable-next-line no-empty-pattern
  async ({ newPassword }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let reset: boolean | undefined;

      if (userAuth?.auth_token) {
        reset = await new UserService(userAuth?.auth_token).resetUserPassword(
          userAuth.id,
          newPassword
        );
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!reset) {
        return rejectWithValue('Error reseting password');
      }

      return reset;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateUser = createAsyncThunk<any, {}, ThunkApiType<RootState>>(
  'updateUser',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      user: { user },
    } = getState();

    try {
      let update: boolean | undefined;

      if (userAuth?.auth_token && user) {
        const userModelInput: UserModelInput = mapUser(user);

        update = await new UserService(userAuth?.auth_token).updateUser(
          userAuth.id,
          userModelInput
        );
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!update) {
        return rejectWithValue('Error updating user');
      }

      return [update];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const syncUser = createAsyncThunk<any, {}, ThunkApiType<RootState>>(
  'syncUser',
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      user: { user },
    } = getState();

    // 1. Hard Failure: No token
    if (!userAuth?.auth_token) {
      return rejectWithValue('No access token found');
    }

    // 2. Success Case: User is already synced, nothing to do!
    if (user?.synced || !user) {
      return ['no_update_required'];
    }

    if ('synced' in user === false) {
      return ['no_update_required'];
    }

    // 3. Action Case: User needs syncing
    try {
      const userModelInput: UserModelInput = mapUser(user);
      const update = await new UserService(userAuth.auth_token).updateUser(
        userAuth.id,
        userModelInput
      );

      if (!update) return rejectWithValue('Error updating user');
      return [update];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getUserSyncStatus = createAsyncThunk<
  any,
  {},
  ThunkApiType<RootState>
>(
  'getUserSyncStatus',
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      settings: { lastDataSync },
      classroomData: { classroom },
    } = getState();

    try {
      let userSyncStatus: UserSyncStatus;

      if (userAuth?.auth_token) {
        userSyncStatus = await new UserService(
          userAuth?.auth_token
        ).getUserSyncStatus(
          new Date(lastDataSync) || new Date(),
          classroom?.id || newGuid()
        );
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      return userSyncStatus;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type AddUserRequest = {
  user: UserDto;
};

export const addUser = createAsyncThunk<
  UserDto,
  AddUserRequest,
  ThunkApiType<RootState>
>('addUser', async ({ user }, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
  } = getState();

  try {
    if (userAuth?.auth_token) {
      const userModelInput: UserModelInput = mapUser(user);

      return await new UserService(userAuth?.auth_token).addUser(
        userModelInput
      );
    } else {
      return rejectWithValue('no access token, profile check required');
    }
  } catch (err) {
    return rejectWithValue(err);
  }
});

const mapConsent = (userConsent: UserConsentDto): UserConsentInput => ({
  Id: userConsent.id,
  ConsentId: userConsent.consentId,
  ConsentType: userConsent.consentType,
  CreatedUserId: userConsent.createdUserId,
  UserId: userConsent.userId,
  IsActive: true,
});

const mapUser = (user: Partial<UserDto>): UserModelInput => ({
  isSouthAfricanCitizen: user.isSouthAfricanCitizen || false,
  idNumber: user.idNumber && user.idNumber.length > 0 ? user.idNumber : null,
  verifiedByHomeAffairs: user.verifiedByHomeAffairs || false,
  dateOfBirth: user.dateOfBirth,
  genderId: user.genderId ? user.genderId : null,
  raceId: user.raceId ? user.raceId : null,
  firstName: user.firstName,
  surname: user.surname,
  contactPreference: user.contactPreference,
  phoneNumber: user.phoneNumber,
  email: user.email,
  profileImageUrl: user.profileImageUrl,
  emergencyContactFirstName: user.emergencyContactFirstName
    ? user.emergencyContactFirstName
    : null,
  emergencyContactSurname: user.emergencyContactSurname
    ? user.emergencyContactSurname
    : null,
  emergencyContactPhoneNumber: user.emergencyContactPhoneNumber
    ? user.emergencyContactPhoneNumber
    : null,
  languageId:
    user.languageId && user.languageId.length ? user.languageId : null,
  whatsAppNumber: user.whatsappNumber ? user.whatsappNumber : null,
  resetData: user.resetData || false,
  whatsAppConsent: user.whatsAppConsent || false,
});
